import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { GlassCard, CardContent, CardHeader, CardTitle, Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Clock,
  LogIn,
  LogOut,
  MapPin,
  FileText,
  Clock3,
  CalendarDays,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Fingerprint,
  Filter,
  IndianRupee,
  Coins,
  Send,
  Loader2,
  Bike
} from 'lucide-react';
import { attendanceService, dailyReportService, expenseService, staffService, AttendanceRecord, DailyReport, Staff, Expense } from '@/lib/storage';
import { useToast } from '@/hooks/use-toast';
import SelfAttendanceModal from '@/components/SelfAttendanceModal';
import { getLocalDateString } from '@/lib/utils';

const StaffAttendancePage = () => {
  const { session } = useAuth();
  const { toast } = useToast();
  const [attendanceLogs, setAttendanceLogs] = useState<AttendanceRecord[]>([]);
  const [todayRecord, setTodayRecord] = useState<AttendanceRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyDateFilter, setHistoryDateFilter] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all');

  // Claim Expense States
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [claimDate, setClaimDate] = useState('');
  const [claimAmount, setClaimAmount] = useState('');
  const [claimRemark, setClaimRemark] = useState('');
  const [claimCategory, setClaimCategory] = useState('Other Exp');
  const [claimKm, setClaimKm] = useState('');
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [claiming, setClaiming] = useState(false);
  const [expenseLogs, setExpenseLogs] = useState<Expense[]>([]);

  // KM Travel Logging States
  const [staffProfile, setStaffProfile] = useState<Staff | null>(null);
  const [isKmModalOpen, setIsKmModalOpen] = useState(false);
  const [targetRecordId, setTargetRecordId] = useState('');
  const [kmInput, setKmInput] = useState('');
  const [loggingKm, setLoggingKm] = useState(false);

  const openKmModal = (record: AttendanceRecord) => {
    setTargetRecordId(record.id);
    setKmInput(record.kmTraveled ? record.kmTraveled.toString() : '');
    setIsKmModalOpen(true);
  };

  const handleLoggingKm = async () => {
    const kms = parseFloat(kmInput);
    if (isNaN(kms) || kms < 0) {
      toast({
        title: 'Invalid Kilometers',
        description: 'Please enter a valid kilometer number.',
        variant: 'destructive',
      });
      return;
    }

    setLoggingKm(true);
    try {
      await attendanceService.updateKm(targetRecordId, kms);
      toast({
        title: 'KM Logged Successfully',
        description: `Successfully logged ${kms} KM traveled.`,
      });
      setIsKmModalOpen(false);
      loadData();
    } catch (e) {
      console.error(e);
      toast({
        title: 'Failed to Log KM',
        description: 'Failed to log KM. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoggingKm(false);
    }
  };

  const openClaimModal = (date: string) => {
    setClaimDate(date);
    setClaimAmount('');
    setClaimRemark('');
    setClaimCategory('Other Exp');
    setClaimKm('');
    setIsClaimModalOpen(true);
  };

  const handleClaimKmChange = (val: string) => {
    setClaimKm(val);
    const rate = staffProfile?.bikeRatePerKm || 0;
    if (val && !isNaN(parseFloat(val)) && parseFloat(val) > 0) {
      const calc = rate > 0 ? (parseFloat(val) * rate).toFixed(2) : '';
      setClaimAmount(calc);
    } else {
      setClaimAmount('');
    }
  };

  const handleSubmittingClaim = async () => {
    const amt = parseFloat(claimAmount);
    if (isNaN(amt) || amt <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid amount greater than 0.',
        variant: 'destructive',
      });
      return;
    }

    if (!session?.userId) return;

    let finalRemark = claimRemark.trim();
    const rate = staffProfile?.bikeRatePerKm || 0;
    if (claimCategory === 'Travel Allowance' && claimKm && parseFloat(claimKm) > 0) {
      const kmInfo = `${claimKm} KM @ ₹${rate}/km`;
      finalRemark = finalRemark ? `${finalRemark} (${kmInfo})` : `Travel Allowance (${kmInfo})`;
    } else if (!finalRemark) {
      finalRemark = `Attendance Claim: ${claimDate}`;
    }

    setClaiming(true);
    try {
      await expenseService.create({
        staffId: session.userId,
        staffName: session.name,
        date: claimDate,
        amount: amt,
        category: claimCategory,
        remark: finalRemark,
      });
      toast({
        title: 'Claim Submitted',
        description: `Your expense claim of ₹${amt.toLocaleString('en-IN')} has been submitted for approval.`,
      });
      setIsClaimModalOpen(false);
    } catch (e) {
      console.error(e);
      toast({
        title: 'Submission Failed',
        description: 'Failed to submit your expense claim. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setClaiming(false);
    }
  };

  useEffect(() => {
    if (session?.userId) {
      loadData();
      const interval = setInterval(() => loadData(true), 10000);
      return () => clearInterval(interval);
    }
  }, [session]);

  const loadData = async (silent = false) => {
    if (!session?.userId) return;
    if (!silent) setLoading(true);
    try {
      const [logs, profile, expenses] = await Promise.all([
        attendanceService.getByStaffId(session.userId),
        staffService.getById(session.userId),
        expenseService.getAll({ staffId: session.userId })
      ]);
      setStaffProfile(profile || null);
      setExpenseLogs(expenses || []);
      
      // Sort logs by date descending
      const sortedLogs = logs.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setAttendanceLogs(sortedLogs);

      // Find today's record
      const today = getLocalDateString();
      const todayRec = sortedLogs.find(l => l.date === today);
      setTodayRecord(todayRec || null);
    } catch (error) {
      console.error("Failed to load attendance logs", error);
      if (!silent) {
        toast({
          title: 'Error loading data',
          description: 'Failed to fetch logs and reports.',
          variant: 'destructive'
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleAction = (type: 'checkin' | 'checkout') => {
    if (type === 'checkin') {
      if (todayRecord && todayRecord.checkIn) {
        toast({
          title: 'Already Checked In',
          description: 'You have already checked in for today.',
          variant: 'default',
        });
        return;
      }
    } else {
      if (!todayRecord || !todayRecord.checkIn) {
        toast({
          title: 'Check-in Required',
          description: 'You must check in first before checking out.',
          variant: 'destructive',
        });
        return;
      }
      if (todayRecord.checkOut) {
        toast({
          title: 'Already Checked Out',
          description: 'You have already checked out for today.',
          variant: 'default',
        });
        return;
      }
    }
    
    setIsModalOpen(true);
  };

  const formatTime = (isoString: string | undefined): string => {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getVerificationBadge = (record: AttendanceRecord) => {
    const status = record.verification || 'verified';
    switch (status) {
      case 'pending':
        return <Badge variant="warning" className="rounded-lg text-[9px] font-bold uppercase tracking-wider">Pending Location Approval</Badge>;
      case 'approved':
        return <Badge variant="success" className="rounded-lg text-[9px] font-bold uppercase tracking-wider">Outside Location Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive" className="rounded-lg text-[9px] font-bold uppercase tracking-wider">Outside Location Rejected</Badge>;
      default:
        return <Badge variant="secondary" className="rounded-lg text-[9px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-350 uppercase tracking-wider">Verified Location</Badge>;
    }
  };

  // Stagger animations
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 350, damping: 25 } }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <span className="w-9 h-9 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm font-semibold text-slate-400">Loading data...</p>
        </div>
      </div>
    );
  }

  const todayDateStr = getLocalDateString();
  const filteredLogs = attendanceLogs.filter(log => {
    // Exclude today's attendance log from the history list
    if (log.date === todayDateStr) return false;

    const matchesDate = historyDateFilter ? log.date === historyDateFilter : true;
    const matchesStatus = historyStatusFilter === 'all' ? true : log.status === historyStatusFilter;
    return matchesDate && matchesStatus;
  });

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="space-y-6"
    >
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">Attendance Control</h2>
        <p className="text-sm text-slate-500 font-medium">Verify shifts, record daily clock-ins/outs and track historical registers.</p>
      </div>

      {/* Premium Shift Status Card */}
      <motion.div variants={itemVariants}>
        <Card className="border-slate-200 dark:border-slate-800 shadow-md rounded-2xl bg-white dark:bg-slate-900 relative overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-emerald-500 via-indigo-500 to-indigo-650" />
          <CardContent className="p-6 space-y-6">
            
            {/* Header / Main Action Row */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pb-6 border-b border-slate-100 dark:border-slate-800/60">
              <div className="flex items-center gap-4 text-center sm:text-left">
                <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0 mx-auto">
                  <Fingerprint className="w-7 h-7" />
                </div>
                <div className="text-left">
                  <h3 className="font-extrabold text-base text-slate-800 dark:text-slate-100">Daily Attendance</h3>
                  <p className="text-xs text-slate-400 dark:text-slate-500 font-semibold mt-0.5 leading-relaxed">
                    {todayRecord?.checkIn && todayRecord?.checkOut 
                      ? 'Shift completed for today!' 
                      : todayRecord?.checkIn 
                        ? 'Shift active. Don\'t forget to check out.' 
                        : 'Tap below to start your shift.'}
                  </p>
                </div>
              </div>

              {/* Actions Grid */}
              <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
                <Button
                  variant="royal"
                  disabled={!!(todayRecord && todayRecord.checkIn)}
                  onClick={() => handleAction('checkin')}
                  className="flex-1 sm:flex-none rounded-xl font-bold gap-2 px-5 h-11 bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/10 disabled:opacity-50"
                >
                  <LogIn className="w-4 h-4" /> Check In
                </Button>
                <Button
                  variant="royal"
                  disabled={!todayRecord?.checkIn || !!todayRecord?.checkOut || todayRecord?.verification === 'rejected'}
                  onClick={() => handleAction('checkout')}
                  className="flex-1 sm:flex-none rounded-xl font-bold gap-2 px-5 h-11 shadow-lg shadow-indigo-600/10 disabled:opacity-50"
                >
                  <LogOut className="w-4 h-4" /> Check Out
                </Button>
              </div>
            </div>

            {/* Today's Progress Timeline */}
            <div className="space-y-4">
              <h4 className="text-xs font-extrabold uppercase tracking-widest text-slate-400">Today's Progress</h4>
              <div className="relative pl-6 border-l-2 border-slate-100 dark:border-slate-800 space-y-6">
                
                {/* Timeline Item 1: Check In */}
                <div className="relative">
                  <div className={`absolute -left-[31px] top-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center bg-white dark:bg-slate-900 ${
                    todayRecord?.checkIn 
                      ? 'border-emerald-500 text-emerald-500' 
                      : 'border-slate-200 dark:border-slate-800 text-slate-400'
                  }`}>
                    <LogIn className="w-3 h-3" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Clock In</p>
                    <p className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 mt-0.5">
                      {todayRecord?.checkIn 
                        ? `Recorded at ${formatTime(todayRecord.checkIn || todayRecord.clockIn)}` 
                        : 'Waiting for scan…'}
                    </p>
                  </div>
                </div>

                {/* Timeline Item 2: Location Geofence check */}
                {todayRecord?.checkIn && (
                  <div className="relative animate-fadeIn">
                    <div className={`absolute -left-[31px] top-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center bg-white dark:bg-slate-900 ${
                      todayRecord.verification === 'approved' || todayRecord.verification === 'verified' || !todayRecord.verification
                        ? 'border-emerald-500 text-emerald-500'
                        : todayRecord.verification === 'pending'
                          ? 'border-amber-500 text-amber-500'
                          : 'border-rose-500 text-rose-500'
                    }`}>
                      <MapPin className="w-3 h-3" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Location Verification</p>
                      <div className="flex items-center gap-2 mt-1">
                        {getVerificationBadge(todayRecord)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Timeline Item 3: Check Out */}
                <div className="relative">
                  <div className={`absolute -left-[31px] top-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center bg-white dark:bg-slate-900 ${
                    todayRecord?.checkOut 
                      ? 'border-indigo-500 text-indigo-500' 
                      : 'border-slate-200 dark:border-slate-800 text-slate-400'
                  }`}>
                    <LogOut className="w-3 h-3" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Clock Out</p>
                    <p className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 mt-0.5">
                      {todayRecord?.checkOut 
                        ? `Recorded at ${formatTime(todayRecord.checkOut || todayRecord.clockOut)}` 
                        : todayRecord?.checkIn 
                          ? 'Shift in progress…' 
                          : 'Waiting for scan…'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </CardContent>
        </Card>
      </motion.div>

      {/* Today's Log Card (if todayRecord exists) */}
      {todayRecord && (
        <motion.div variants={itemVariants}>
          <GlassCard className="border-emerald-500/35 dark:border-emerald-500/20 shadow-md rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl relative overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-1 bg-emerald-500" />
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                <div className="flex items-center gap-2 font-bold text-slate-800 dark:text-slate-100">
                  <Calendar className="w-4.5 h-4.5 text-emerald-500 animate-pulse" />
                  Today's Attendance Log
                </div>
                {getVerificationBadge(todayRecord)}
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Clock In</p>
                  <p className="font-bold text-slate-700 dark:text-slate-300 mt-1 flex items-center gap-1">
                    <LogIn className="w-3.5 h-3.5 text-emerald-500" />
                    {formatTime(todayRecord.checkIn || todayRecord.clockIn)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Clock Out</p>
                  <p className="font-bold text-slate-700 dark:text-slate-300 mt-1 flex items-center gap-1">
                    <LogOut className="w-3.5 h-3.5 text-indigo-500" />
                    {formatTime(todayRecord.checkOut || todayRecord.clockOut)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Duration</p>
                  <p className="font-bold text-slate-700 dark:text-slate-300 mt-1 flex items-center gap-1">
                    <Clock3 className="w-3.5 h-3.5 text-amber-500" />
                    {todayRecord.workingHours ? `${todayRecord.workingHours.toFixed(1)} hrs` : '—'}
                  </p>
                </div>
              </div>

              {staffProfile?.travelsByBike && (
                <div className="grid grid-cols-2 gap-2 text-xs border-t border-slate-100 dark:border-slate-800/60 pt-3 mt-1">
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">KM Traveled</p>
                    <p className="font-bold text-slate-700 dark:text-slate-300 mt-1 flex items-center gap-1">
                      <Bike className="w-3.5 h-3.5 text-sky-500" />
                      {todayRecord.kmTraveled ? `${todayRecord.kmTraveled} KM` : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Travel Cost</p>
                    <p className="font-bold text-slate-700 dark:text-slate-300 mt-1 flex items-center gap-1">
                      <IndianRupee className="w-3 h-3 text-emerald-500" />
                      {todayRecord.travelExpenses ? `₹${todayRecord.travelExpenses.toFixed(2)}` : '—'}
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons for Today's Card */}
              {(() => {
                const hasPendingTravelClaim = expenseLogs.some(
                  e => e.date === todayRecord.date && e.status === 'pending' && e.remark?.startsWith('Travel Allowance')
                );
                const hasPendingGeneralClaim = expenseLogs.some(
                  e => e.date === todayRecord.date && e.status === 'pending' && !e.remark?.startsWith('Travel Allowance')
                );

                return (
                  <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800/60 pt-3 mt-1">
                    {todayRecord.latitude && todayRecord.longitude && todayRecord.verification !== 'verified' ? (
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                        <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <a
                          href={`https://www.google.com/maps?q=${todayRecord.latitude},${todayRecord.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline font-bold"
                        >
                          GPS Loc
                        </a>
                      </div>
                    ) : (
                      <div />
                    )}
                    <div className="flex items-center gap-1.5">
                      {staffProfile?.travelsByBike && todayRecord.checkOut && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={hasPendingTravelClaim}
                          onClick={() => openKmModal(todayRecord)}
                          className="h-7 px-2.5 rounded-lg border-sky-200 dark:border-sky-800/40 text-[10px] font-bold text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/20 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Bike className="w-3.5 h-3.5 text-sky-500" />
                          {hasPendingTravelClaim ? 'Travel Pending' : (todayRecord.kmTraveled ? 'Edit KM' : 'Log KM')}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={hasPendingGeneralClaim}
                        onClick={() => openClaimModal(todayRecord.date)}
                        className="h-7 px-2.5 rounded-lg border-indigo-200 dark:border-indigo-800/40 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/20 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Coins className="w-3.5 h-3.5 text-indigo-500" />
                        {hasPendingGeneralClaim ? 'Claim Pending' : 'Add Claim'}
                      </Button>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </GlassCard>
        </motion.div>
      )}

      {/* Main Records Section */}
      <motion.div variants={itemVariants} className="space-y-4">
        <Button
          variant="outline"
          onClick={() => setShowHistory(!showHistory)}
          className="w-full h-11.5 rounded-xl font-bold flex items-center justify-center gap-2 border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60 shadow-sm"
        >
          <CalendarDays className="w-4.5 h-4.5 text-indigo-500" />
          {showHistory ? 'Hide Attendance History' : 'View Attendance History'}
        </Button>

        <AnimatePresence>
          {showHistory && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden space-y-4"
            >
              {/* Filters Toolbar */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                {/* Date Filter */}
                <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 dark:border-slate-800 px-3 bg-white/50 dark:bg-slate-950/40 w-full sm:max-w-[240px] h-11">
                  <CalendarDays className="w-4.5 h-4.5 text-slate-400 shrink-0" />
                  <Input
                    type="date"
                    value={historyDateFilter}
                    onChange={(e) => setHistoryDateFilter(e.target.value)}
                    className="border-none bg-transparent focus-visible:ring-0 p-0 text-sm font-medium w-full"
                  />
                </div>

                {/* Status Filter */}
                <Select value={historyStatusFilter} onValueChange={historyStatusFilter => setHistoryStatusFilter(historyStatusFilter)}>
                  <SelectTrigger className="h-11 w-full sm:w-[200px] rounded-xl border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-900/40">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-slate-400" />
                      <SelectValue placeholder="All Status" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="present">On Time</SelectItem>
                    <SelectItem value="late">Late</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                  </SelectContent>
                </Select>

                {/* Clear Filters Button */}
                {(historyDateFilter || historyStatusFilter !== 'all') && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setHistoryDateFilter('');
                      setHistoryStatusFilter('all');
                    }}
                    className="h-11 rounded-xl text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>

              {filteredLogs.length === 0 ? (
                <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
                  <CardContent className="py-12 text-center text-slate-400 font-medium">
                    No matching attendance logs found.
                  </CardContent>
                </GlassCard>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  {filteredLogs.map((record) => {
                    const hasPendingTravelClaim = expenseLogs.some(
                      e => e.date === record.date && e.status === 'pending' && e.remark?.startsWith('Travel Allowance')
                    );
                    const hasPendingGeneralClaim = expenseLogs.some(
                      e => e.date === record.date && e.status === 'pending' && !e.remark?.startsWith('Travel Allowance')
                    );

                    return (
                      <GlassCard key={record.id} className="border-slate-200/50 dark:border-slate-800 shadow-sm rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl relative overflow-hidden">
                        <CardContent className="p-5 space-y-4">
                          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                            <div className="flex items-center gap-2 font-bold text-slate-800 dark:text-slate-100">
                              <Calendar className="w-4.5 h-4.5 text-indigo-500" />
                              {new Date(record.date + 'T00:00:00').toLocaleDateString(undefined, {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </div>
                            {getVerificationBadge(record)}
                          </div>

                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Clock In</p>
                              <p className="font-bold text-slate-700 dark:text-slate-300 mt-1 flex items-center gap-1">
                                <LogIn className="w-3.5 h-3.5 text-emerald-500" />
                                {formatTime(record.checkIn || record.clockIn)}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Clock Out</p>
                              <p className="font-bold text-slate-700 dark:text-slate-300 mt-1 flex items-center gap-1">
                                <LogOut className="w-3.5 h-3.5 text-indigo-500" />
                                {formatTime(record.checkOut || record.clockOut)}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Duration</p>
                              <p className="font-bold text-slate-700 dark:text-slate-300 mt-1 flex items-center gap-1">
                                <Clock3 className="w-3.5 h-3.5 text-amber-500" />
                                {record.workingHours ? `${record.workingHours.toFixed(1)} hrs` : '—'}
                              </p>
                            </div>
                          </div>

                          {staffProfile?.travelsByBike && (
                            <div className="grid grid-cols-2 gap-2 text-xs border-t border-slate-100 dark:border-slate-800/60 pt-3 mt-1">
                              <div>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">KM Traveled</p>
                                <p className="font-bold text-slate-700 dark:text-slate-300 mt-1 flex items-center gap-1">
                                  <Bike className="w-3.5 h-3.5 text-sky-500" />
                                  {record.kmTraveled ? `${record.kmTraveled} KM` : '—'}
                                </p>
                              </div>
                              <div>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Travel Cost</p>
                                <p className="font-bold text-slate-700 dark:text-slate-300 mt-1 flex items-center gap-1">
                                  <IndianRupee className="w-3 h-3 text-emerald-500" />
                                  {record.travelExpenses ? `₹${record.travelExpenses.toFixed(2)}` : '—'}
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800/60 pt-3 mt-1">
                            {record.latitude && record.longitude && record.verification !== 'verified' ? (
                              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                                <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                <a
                                  href={`https://www.google.com/maps?q=${record.latitude},${record.longitude}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 dark:text-blue-400 hover:underline font-bold"
                                >
                                  GPS Loc
                                </a>
                              </div>
                            ) : (
                              <div />
                            )}
                            <div className="flex items-center gap-1.5">
                              {staffProfile?.travelsByBike && record.checkOut && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={hasPendingTravelClaim}
                                  onClick={() => openKmModal(record)}
                                  className="h-7 px-2.5 rounded-lg border-sky-200 dark:border-sky-800/40 text-[10px] font-bold text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/20 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Bike className="w-3.5 h-3.5 text-sky-500" />
                                  {hasPendingTravelClaim ? 'Travel Pending' : (record.kmTraveled ? 'Edit KM' : 'Log KM')}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={hasPendingGeneralClaim}
                                onClick={() => openClaimModal(record.date)}
                                className="h-7 px-2.5 rounded-lg border-indigo-200 dark:border-indigo-800/40 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/20 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Coins className="w-3.5 h-3.5 text-indigo-500" />
                                {hasPendingGeneralClaim ? 'Claim Pending' : 'Add Claim'}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </GlassCard>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Scanner Modal */}
      <SelfAttendanceModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          loadData();
        }}
      />

      {/* Log KM Modal */}
      <Dialog open={isKmModalOpen} onOpenChange={setIsKmModalOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden p-0">
          <div className="bg-gradient-to-r from-sky-500 via-sky-600 to-sky-700 px-6 py-5 text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Bike className="w-5.5 h-5.5" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-white">Log Travel Kilometers</DialogTitle>
              <DialogDescription className="text-xs text-sky-100 font-medium">Log or update traveled kilometers for this attendance date.</DialogDescription>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Traveled Kilometers (KM)</label>
              <div className="relative">
                <Bike className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-450" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={kmInput}
                  onChange={(e) => setKmInput(e.target.value)}
                  className="pl-9 h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40"
                />
              </div>
            </div>
            
            {parseFloat(kmInput) > 0 && (
              <div className="p-3 bg-sky-500/5 border border-sky-500/10 rounded-xl text-xs font-bold text-sky-600 dark:text-sky-400 animate-in fade-in duration-200">
                Calculated travel allowance: ₹{((parseFloat(kmInput) || 0) * (staffProfile?.bikeRatePerKm || 0)).toFixed(2)} (₹{staffProfile?.bikeRatePerKm || 0}/km)
              </div>
            )}
          </div>

          <DialogFooter className="bg-slate-50 dark:bg-slate-950/40 px-6 py-4 border-t border-slate-100 dark:border-slate-850/60 flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setIsKmModalOpen(false)}
              className="rounded-xl border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-850 font-bold text-slate-700 dark:text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleLoggingKm}
              disabled={loggingKm || !kmInput}
              className="rounded-xl font-bold bg-sky-600 hover:bg-sky-500 text-white flex items-center justify-center gap-1.5 shadow-md shadow-sky-600/10"
            >
              {loggingKm ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Save KM
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Claim Modal */}
      <Dialog open={isClaimModalOpen} onOpenChange={setIsClaimModalOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden p-0">
          <div className="bg-gradient-to-r from-indigo-500 via-indigo-600 to-indigo-700 px-6 py-5 text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Coins className="w-5.5 h-5.5" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-white">Claim Daily Expense</DialogTitle>
              <DialogDescription className="text-xs text-indigo-100 font-medium">Submit an expense claim for this attendance date.</DialogDescription>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Attendance Date</label>
              <Input
                type="text"
                value={claimDate ? new Date(claimDate + 'T00:00:00').toLocaleDateString(undefined, {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : ''}
                disabled
                className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 font-bold text-slate-700 dark:text-slate-300"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Category *</label>
              <select
                value={claimCategory}
                onChange={(e) => {
                  setClaimCategory(e.target.value);
                  setClaimKm('');
                }}
                className="w-full h-10.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40 px-3 text-sm font-medium"
              >
                <option value="Other Exp">Other Exp</option>
                <option value="Travel Allowance">Travel Allowance</option>
                <option value="Food">Food</option>
                <option value="Office Supplies">Office Supplies</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>

            {claimCategory === 'Travel Allowance' ? (
              <div className="p-3.5 rounded-2xl bg-purple-50/60 dark:bg-purple-950/20 border border-purple-200/50 dark:border-purple-800/30 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
                    <Bike className="w-4 h-4 text-purple-600" />
                    Travel Allowance Options
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                    Rate: ₹{staffProfile?.bikeRatePerKm || 0}/km
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Kilometers (KM)</label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      placeholder="e.g. 15"
                      value={claimKm}
                      onChange={(e) => handleClaimKmChange(e.target.value)}
                      className="h-9 rounded-xl border-purple-200 dark:border-purple-900/50 bg-white dark:bg-slate-950/40 text-xs font-medium"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Amount (₹)</label>
                    <div className="relative">
                      <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-450" />
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={claimAmount}
                        onChange={(e) => setClaimAmount(e.target.value)}
                        disabled={!!claimKm && parseFloat(claimKm) > 0}
                        className="pl-8 h-9 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40 text-xs font-medium disabled:opacity-60 disabled:bg-slate-100 dark:disabled:bg-slate-800"
                      />
                    </div>
                  </div>
                </div>

                {claimKm && parseFloat(claimKm) > 0 ? (
                  <p className="text-[11px] font-medium text-purple-600 dark:text-purple-400 flex items-center gap-1">
                    ✓ {claimKm} KM × ₹{staffProfile?.bikeRatePerKm || 0}/km = ₹{claimAmount} (Amount section auto-calculated & disabled)
                  </p>
                ) : (
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">
                    Fill KM to auto-calculate amount, or enter Amount directly for manual travel claim.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-1.5">
                <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Amount (₹)</label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-450" />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={claimAmount}
                    onChange={(e) => setClaimAmount(e.target.value)}
                    className="pl-9 h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40"
                  />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Remark / Purpose</label>
              <Input
                type="text"
                placeholder="e.g. Site travel fuel, Client lunch"
                value={claimRemark}
                onChange={(e) => setClaimRemark(e.target.value)}
                className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40"
              />
            </div>
          </div>

          <DialogFooter className="bg-slate-50 dark:bg-slate-950/40 px-6 py-4 border-t border-slate-100 dark:border-slate-850/60 flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setIsClaimModalOpen(false)}
              className="rounded-xl border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-850 font-bold text-slate-700 dark:text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmittingClaim}
              disabled={claiming || !claimAmount}
              className="rounded-xl font-bold bg-indigo-600 hover:bg-indigo-500 text-white flex items-center justify-center gap-1.5 shadow-md shadow-indigo-600/10"
            >
              {claiming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Submit Claim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default StaffAttendancePage;
